import { HTMLAttributes, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { useRouter } from 'next/router'

import Container from '@/components/base/Container'
import Cta from '@/components/base/Cta'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import ScrollingIcon from '@/components/base/ScrollingIcon'
import Typography from '@/components/base/Typography'
import { useLenis } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { useLoaderStore } from '@/store/loader'

import { heroResize } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPPageData } from '@/types'

export type HeroResizeProps = HTMLAttributes<HTMLElement> &
  Partial<{
    image: WPImage
    image_desktop: WPImage
    image_mobile: WPImage
    pretitle: string
    title: string
    text: string
    link: WPLink
    theme: WPPageData['acf']['theme']
    alignment: 'left' | 'right'
    is_text_big: boolean
    is_boxed: boolean
    use_square_layout: boolean
  }>

const HeroResize = ({
  image,
  image_desktop,
  image_mobile,
  pretitle,
  title,
  text,
  link,
  theme,
  alignment,
  is_text_big,
  use_square_layout
}: HeroResizeProps) => {
  const router = useRouter()
  const { strings } = useStore()
  const wrapper = useRef<HTMLDivElement>(null!)
  const grid = useRef<HTMLDivElement>(null!)
  const loaded = useLoaderStore((state) => state.pageLoaded)
  const { lenis } = useLenis()

  useGSAP(
    () => {
      if (!lenis || !loaded) return
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper.current,
          invalidateOnRefresh: true,
          scrub: 1,
          start: 'top top',
          end: () => window.innerHeight * 0.5
        }
      })
      timeline.fromTo(
        '[data-motion-wrapper]',
        {
          scaleX: 1
        },
        {
          scaleX: () => grid.current.offsetWidth / window.innerWidth,
          duration: 4,
          ease: 'linear'
        }
      )
      timeline.fromTo(
        '[data-motion-picture]',
        { scaleX: 1 },
        {
          scaleX: () => window.innerWidth / grid.current.offsetWidth,
          duration: 4,
          ease: 'linear'
        },
        0
      )
      timeline.add(
        gsap.fromTo(
          '[data-motion-bottom]',
          { autoAlpha: 1 },
          {
            autoAlpha: 0,
            duration: 1,
            ease: 'linear'
          }
        ),
        1
      )
      return () => {
        timeline.kill()
      }
    },
    {
      dependencies: [lenis, loaded],
      scope: wrapper,
      revertOnUpdate: true
    }
  )

  const handleSectionClick = () => {
    if (link && link.url) router.push(link.url, link.url, { scroll: false })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (link && link.url && e.key === 'Enter')
      router.push(link.url, link.url, { scroll: false })
  }

  return (
    <Motion animation={heroResize}>
      {(motionRef) => (
        <section
          ref={(ref) => {
            // @ts-ignore
            motionRef.current = ref
            // @ts-ignore
            wrapper.current = ref
          }}
          className={cn([
            'relative h-svh',
            {
              'h-svh': use_square_layout,
              'bg-white': theme === 'light',
              'bg-dark-600': theme === 'dark',
              'cursor-pointer': link && link.url
            }
          ])}
          onClick={link && link.url ? handleSectionClick : undefined}
          onKeyDown={link && link.url ? handleKeyDown : undefined}
          role={link && link.url ? 'link' : undefined}
          tabIndex={link && link.url ? 0 : undefined}
        >
          <div
            className={cn([
              'relative h-full w-full origin-center overflow-hidden will-change-transform',
              'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/30',
              { 'aspect-h-1 aspect-w-1': use_square_layout }
            ])}
            data-motion-wrapper
          >
            {/* Desktop Image */}
            {(image_desktop?.url || image?.url) && (
              <Image
                source={image_desktop || image}
                fill
                fit="cover"
                className="hidden h-full w-full md:block"
                data-motion-picture
              />
            )}
            {/* Mobile Image */}
            {(image_mobile?.url || image?.url) && (
              <Image
                source={image_mobile || image}
                fill
                fit="cover"
                className="block h-full w-full md:hidden"
                data-motion-picture
              />
            )}
          </div>

          <Container className="absolute left-0 top-0 z-10 h-full w-full text-white">
            <Grid
              className={cn('h-full px-gutter md:px-0', {
                'flex items-center justify-center': is_text_big
              })}
              ref={grid}
              data-motion-grid
            >
              {is_text_big ? (
                <Typography
                  variant="h1"
                  component="h1"
                  className="col-span-4 text-center md:col-span-6 lg:col-span-10 lg:col-start-2"
                  data-motion-title
                >
                  {title}
                </Typography>
              ) : (
                <div
                  className={cn(
                    'col-span-5 col-start-1 flex h-full flex-col justify-center gap-[3.2rem] sm:col-span-4 sm:col-start-2 lg:col-span-6 lg:col-start-2 xl:col-span-4 xl:col-start-2',
                    alignment === 'right' &&
                      'md:col-start-2 lg:col-start-7 xl:col-start-8'
                  )}
                >
                  {pretitle ? (
                    <Typography
                      variant="label"
                      className="font-extrabold"
                      data-motion-pretitle
                    >
                      {pretitle}
                    </Typography>
                  ) : null}
                  {title ? (
                    <Typography variant="h2" component="h1" data-motion-title>
                      {title}
                    </Typography>
                  ) : null}
                  {text ? (
                    <Typography variant="body" data-motion-paragraph>
                      {text}
                    </Typography>
                  ) : null}
                  {link ? (
                    <Cta
                      link={link}
                      theme="light"
                      className="!text-white"
                      data-motion-cta
                    />
                  ) : null}
                </div>
              )}
            </Grid>
            <div
              className={cn(
                'absolute bottom-gutter left-gutter right-gutter z-10 flex items-end justify-between',
                'lg:bottom-[6rem]'
              )}
              data-motion-bottom
            >
              <ScrollingIcon
                className="lg:absolute lg:bottom-0 lg:left-0"
                theme="dark"
              />
              <Typography
                variant="label"
                className="lg:absolute lg:bottom-0 lg:right-0"
              >
                {strings?.bottom_right_string}
              </Typography>
            </div>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default HeroResize
